import { debounce } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Icon,
  Image,
  Input,
  List,
  Menu,
  Modal,
  Rating,
} from "semantic-ui-react";

import Fuse from "fuse.js";

import { CustomListModal } from "./CustomListModal";
import { createSaveLink } from "./utils";

import "./CustomListBuilder.css";
import { ClearableRating } from "./ClearableRating";

const fuzzySearchList = (playlists) => {
  const allSongs = playlists
    .reduce((acc, list) => {
      list.playlist.playlist.forEach((song) => {
        acc.push({ ...song, album: list });
      });

      return acc;
    }, [])
    .sort((a, b) => a.title.localeCompare(b.title));

  const fuse = new Fuse(allSongs, {
    keys: ["title"],
    threshold: 0.4,
    location: 3,
    distance: 30,
  });

  return [fuse, allSongs];
};

const applyFilter = debounce(
  (fuse, filter) => {
    const accepted = fuse.search(filter).reduce((set, e) => {
      set.add(e.item.id);

      return set;
    }, new Set());

    return (id) => accepted.has(id);
  },
  50,
  { leading: true }
);

function initialSet(defaultList) {
  const set = new Set();

  if (!defaultList) return set;

  defaultList.playlist.forEach((song) => {
    set.add(song.id);
  });

  return set;
}

export function CustomListBuilder({ playlists, open, onDone, defaultList }) {
  const [titleFilter, setTitleFilter] = useState("");
  const [albumFilter, setAlbumFilter] = useState("");
  const [authorFilter, setAuthorFilter] = useState("");
  const [selected, setSelected] = useState(initialSet(defaultList));
  const [renderedItems, setRenderedItems] = useState([]);
  const [requiredStars, setRequiredStars] = useState(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(
    defaultList && defaultList.title ? defaultList.title : "Untitled"
  );

  const [fuse, allSongs] = useMemo(() => fuzzySearchList(playlists), [
    playlists,
  ]);

  useEffect(() => {
    const items = allSongs.map((song) => <SongCard song={song} />);

    setRenderedItems(items.slice(0, 15));

    setTimeout(() => setRenderedItems(items), 3000);
  }, [allSongs]);

  const acceptFilter = useMemo(() => {
    if (titleFilter.length > 1) return applyFilter(fuse, titleFilter);

    return () => true;
  }, [titleFilter, fuse]);

  const acceptStars = useMemo(() => {
    return requiredStars === null
      ? () => true
      : (song) => song.rating === requiredStars;
  }, [requiredStars]);

  const acceptAlbum = useMemo(() => {
    return albumFilter.length > 1
      ? (song) => song.album.playlist.title === albumFilter
      : () => true;
  }, [albumFilter]);

  const acceptAuthor = useMemo(() => {
    return authorFilter.length > 1
      ? (song) => song.authors.some((author) => author === authorFilter)
      : () => true;
  }, [authorFilter]);

  const accept = useMemo(
    () => (song) =>
      acceptStars(song) &&
      acceptFilter(song.id) &&
      acceptAuthor(song) &&
      acceptAlbum(song),
    [acceptAlbum, acceptAuthor, acceptFilter, acceptStars]
  );

  const authors = useMemo(() => {
    const authors = new Set();

    allSongs.forEach((song) => {
      song.authors.forEach((author) => {
        if (authors.has(author)) return;

        authors.add(author);
      });
    });

    return [...authors.values()].sort().map((author) => ({
      key: author,
      text: author,
      value: author,
    }));
  }, [allSongs]);

  const albums = useMemo(() => {
    const albums = new Set();

    playlists.forEach((album) => albums.add(album.playlist.title));

    return [...albums.values()].sort().map((title) => ({
      key: title,
      text: title,
      value: title,
    }));
  }, [playlists]);

  const [nbSongs, nbMatchingSongs] = useMemo(
    () => [allSongs.length, allSongs.filter(accept).length],
    [allSongs, accept]
  );

  if (!open) return null;

  const startPlay = () => {
    const [playlist] = createSaveLink("Untitled", allSongs, selected);

    onDone(playlist);
  };

  return (
    <div open={true} size="large" className="custom-list-builder">
      <SearchBar
        albums={albums}
        authors={authors}
        albumFilter={albumFilter}
        authorFilter={authorFilter}
        setAlbumFilter={setAlbumFilter}
        setAuthorFilter={setAuthorFilter}
        titleFilter={titleFilter}
        setTitleFilter={setTitleFilter}
        requiredStars={requiredStars}
        setRequiredStars={setRequiredStars}
        onSelectAll={() => {
          const newSet = new Set(selected);

          allSongs.forEach((song) => {
            if (accept(song)) newSet.add(song.id);
          });

          setSelected(newSet);
        }}
        onUnselectAll={() => {
          const newSet = new Set(selected);

          allSongs.forEach((song) => {
            if (accept(song)) newSet.delete(song.id);
          });

          setSelected(newSet);
        }}
        nbSongs={nbSongs}
        nbMatchingSongs={nbMatchingSongs}
      />

      <Modal.Content style={{ overflow: "scroll" }}>
        <List celled relaxed size="big">
          {allSongs.map((song, i) => {
            return (
              <List.Item
                key={song.id}
                style={{ display: accept(song) ? "block" : "none" }}
              >
                <List.Content floated="left">
                  <Checkbox
                    key={song.id}
                    checked={selected.has(song.id)}
                    onClick={() => {
                      const newSet = new Set(selected);

                      if (newSet.has(song.id)) {
                        newSet.delete(song.id);
                      } else {
                        newSet.add(song.id);
                      }

                      setSelected(newSet);
                    }}
                  />
                </List.Content>

                {renderedItems[i]}
              </List.Item>
            );
          })}
        </List>
      </Modal.Content>

      <StatusBar
        songs={allSongs}
        selected={selected}
        onClose={() => onDone()}
        onPlay={() => startPlay()}
        onSave={() => setSaving(true)}
      />

      <CustomListModal
        name={name}
        setName={setName}
        open={saving}
        onClose={() => setSaving(false)}
        allSongs={allSongs}
        selected={selected}
      />
    </div>
  );
}

function SongCard({ song }) {
  return (
    <>
      <List.Content floated="right">
        <Rating
          maxRating={5}
          icon="star"
          size="small"
          rating={song.rating}
          disabled
        />
      </List.Content>
      <Image avatar size="tiny" src={song.album.image} />
      <List.Content>
        <List.Header>{song.title}</List.Header>
        {song.authors.join(", ")}
        <List.Description>{song.album.playlist.title}</List.Description>
      </List.Content>
    </>
  );
}

function StatusBar({ selected, onClose, onPlay, onSave }) {
  return (
    <Menu borderless>
      <Menu.Item>
        <div>
          {selected.size === 0
            ? ""
            : selected.size === 1
            ? `${selected.size} song`
            : `${selected.size} songs`}
        </div>
      </Menu.Item>
      <Menu.Menu position="right">
        <Menu.Item>
          <Button color="blue" onClick={onClose}>
            <Icon name="x" /> Close
          </Button>
        </Menu.Item>
        <Menu.Item>
          <Button color="green" onClick={onSave}>
            <Icon name="save" /> Save
          </Button>
        </Menu.Item>
        <Menu.Item>
          <Button color="red" onClick={onPlay}>
            <Icon name="play" /> Play
          </Button>
        </Menu.Item>
      </Menu.Menu>
    </Menu>
  );
}

function SearchBar({
  albums,
  authors,
  albumFilter,
  authorFilter,
  setAlbumFilter,
  setAuthorFilter,
  titleFilter,
  setTitleFilter,
  requiredStars,
  setRequiredStars,
  onSelectAll,
  onUnselectAll,
  nbSongs,
  nbMatchingSongs,
}) {
  const [allChecked, setAllChecked] = useState(false);

  const hasFilter =
    requiredStars !== null ||
    authorFilter !== "" ||
    titleFilter !== "" ||
    albumFilter !== "";

  useEffect(() => {
    if (!hasFilter) setAllChecked(false);
  }, [hasFilter]);

  return (
    <Menu borderless>
      <Menu.Item>
        <Checkbox
          disabled={!hasFilter}
          checked={hasFilter ? allChecked : false}
          onClick={
            hasFilter
              ? (_ev, data) => {
                  setAllChecked(data.checked);
                  data.checked ? onSelectAll() : onUnselectAll();
                }
              : undefined
          }
        />
      </Menu.Item>

      <Menu.Item
        onClick={() => {
          setTitleFilter("");
          setAlbumFilter("");
          setAuthorFilter("");
          setRequiredStars(null);
        }}
        disabled={!hasFilter}
      >
        {hasFilter ? (
          <Icon.Group>
            <Icon name="filter" color="blue" />
            <Icon corner name="remove" color="blue" />
          </Icon.Group>
        ) : (
          <Icon name="filter" color="black" />
        )}
      </Menu.Item>

      <Menu.Item>
        <Dropdown
          clearable
          search
          selection
          options={albums}
          value={albumFilter}
          placeholder="Search album..."
          onChange={(_ev, { value }) => setAlbumFilter(value)}
        />
      </Menu.Item>
      <Menu.Item>
        <Input
          value={titleFilter}
          placeholder="Search song..."
          icon={<Icon name="x" link onClick={() => setTitleFilter("")} />}
          onChange={(_ev, { value }) => setTitleFilter(value)}
        />
      </Menu.Item>
      <Menu.Item>
        <ClearableRating
          value={requiredStars}
          onChange={setRequiredStars}
          style={{ width: 160 }}
        />
      </Menu.Item>
      <Menu.Item>
        <Dropdown
          clearable
          search
          selection
          options={authors}
          value={authorFilter}
          placeholder="Search author..."
          onChange={(_ev, { value }) => setAuthorFilter(value)}
        />
      </Menu.Item>

      <Menu.Menu position="right">
        <Menu.Item>
          <div>{hasFilter ? `${nbMatchingSongs} / ${nbSongs}` : null}</div>
        </Menu.Item>
      </Menu.Menu>
    </Menu>
  );
}
